import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, Shield, Smartphone, ScanSearch } from 'lucide-react';
import { scrollToId } from '../lib/scroll';

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: '110%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const frameRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const glowX = useSpring(useTransform(mx, [0, 1], ['30%', '70%']), { stiffness: 60, damping: 20 });
  const glowY = useSpring(useTransform(my, [0, 1], ['30%', '70%']), { stiffness: 60, damping: 20 });
  const spotlight = useTransform(
    [glowX, glowY],
    ([x, y]) => `radial-gradient(circle at ${x} ${y}, rgba(34,211,238,0.5), rgba(52,211,153,0.15) 55%, transparent 75%)`
  );

  const handleMouseMove = (e) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  const handleMouseLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-ink"
    >
      {/* Ambient glow + grid */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-[36rem] h-[36rem] bg-cyan-500/15 rounded-full blur-[120px] -top-40 -left-40"></div>
        <div className="absolute w-[32rem] h-[32rem] bg-emerald-500/10 rounded-full blur-[120px] bottom-0 right-0"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-25"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-24 w-full">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          {/* Left content */}
          <div className="lg:col-span-7 text-white">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex items-center gap-3 mb-10"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400 blink-dot"></span>
              <p className="eyebrow text-slate-400">
                Symbionic — Insurance, made human
              </p>
            </motion.div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.04] tracking-tight">
              <MaskedLine delay={0.15}>Making insurance</MaskedLine>
              <MaskedLine delay={0.27}>
                <span className="bg-gradient-to-r from-cyan-400 via-teal-300 to-emerald-400 bg-clip-text text-transparent">
                  simple &amp; reliable
                </span>
              </MaskedLine>
              <MaskedLine delay={0.39}>for everyone.</MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl"
            >
              We turn decades of real insurance experience into smart tools anyone can use — 
              starting with <span className="text-slate-200 font-semibold">VerifAEye</span>, the app that turns your 
              smartphone into a forensic insurance assessor.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                data-testid="hero-cta-explore"
                href="https://verifaeye.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 px-8 py-4 text-base font-semibold text-ink shadow-lg shadow-cyan-500/25 transition-transform duration-300 hover:scale-[1.04]"
              >
                Explore VerifAEye
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                data-testid="hero-cta-learn"
                onClick={() => scrollToId('about')}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/5 hover:border-white/30"
              >
                Our mission
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 border-t rule-dark pt-8"
            >
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-slate-500">iOS &amp; Android</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-slate-500">Bank-level security</span>
              </div>
              <div className="flex items-center gap-2">
                <ScanSearch className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-slate-500">AI-powered inventory</span>
              </div>
            </motion.div>
          </div>

          {/* Right — product shot with 3D tilt */}
          <div className="lg:col-span-5 relative hidden lg:block" style={{ perspective: 1200 }}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.div
                ref={frameRef}
                data-testid="hero-product-frame"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
                className="relative mx-auto w-[300px] xl:w-[340px]"
              >
                {/* Spotlight */}
                <motion.div
                  className="absolute -inset-16 rounded-full blur-[90px] opacity-40 pointer-events-none"
                  style={{ background: spotlight }}
                ></motion.div>

                {/* Clipped phone frame */}
                <div className="relative rounded-[2.5rem] border border-white/10 bg-ink-soft p-2.5 shadow-2xl shadow-cyan-500/10">
                  <div className="rounded-[2rem] overflow-hidden">
                    <img
                      src="/app-scan.jpg"
                      alt="VerifAEye app scanning household items to build an insurance inventory"
                      className="w-full h-auto block"
                    />
                  </div>
                  <div className="absolute inset-0 rounded-[2.5rem] ring-1 ring-inset ring-white/10 pointer-events-none"></div>
                </div>

                {/* Floating chip */}
                <motion.div
                  style={{ transform: 'translateZ(50px)' }}
                  className="absolute -bottom-8 -left-16 bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-xl floating-card"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded-xl flex items-center justify-center">
                      <Shield className="w-5 h-5 text-ink" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Ready in minutes</p>
                      <p className="text-lg font-bold text-slate-900">Full inventory</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId('about')}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500 hover:text-slate-300 transition-colors"
        aria-label="Scroll to content"
      >
        <span className="eyebrow">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-cyan-400/60 to-transparent"></div>
      </motion.button>
    </section>
  );
};

export default Hero;
